import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantsApi } from '@/api/tenants'
import type { InviteTenantUserRequest, PaginationParams, TenantMemberRole, TenantMemberSortColumn } from '@/types'

interface RemoveMutationVars {
  tenantId: string
  userId: string
}

export function useTenantMembers(
  tenantId: string | undefined,
  pagination?: PaginationParams,
  search?: string,
  role?: string,
  status?: string,
  sort?: { sortBy: TenantMemberSortColumn; sortDirection: 'asc' | 'desc' }
) {
  const params = {
    ...pagination,
    ...(search ? { search } : {}),
    ...(role ? { role } : {}),
    ...(status ? { status } : {}),
    ...sort,
  }

  return useQuery({
    queryKey: ['tenant-members', tenantId, params] as const,
    queryFn: () => tenantsApi.getMembers(tenantId!, params),
    enabled: !!tenantId,
  })
}

interface InviteMutationVars {
  tenantId: string
  email: string
  role: TenantMemberRole
}

export function useInviteTenantUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tenantId, ...data }: InviteMutationVars) =>
      tenantsApi.inviteUser(tenantId, data as InviteTenantUserRequest),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', vars.tenantId] })
      queryClient.invalidateQueries({ queryKey: ['tenant', vars.tenantId, 'users'] })
    },
  })
}

export function useRemoveTenantUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tenantId, userId }: RemoveMutationVars) =>
      tenantsApi.removeUser(tenantId, userId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', vars.tenantId] })
      queryClient.invalidateQueries({ queryKey: ['tenant', vars.tenantId, 'users'] })
    },
  })
}
